import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const contactSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Mitigation Restoration Services Houston TX',
    description: 'Contact Mitigation Restoration Services for 24/7 emergency cleanup, property restoration, and pipe leak repair in Houston TX. Call or submit a request online.',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact` },
    ],
  },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitting(true);
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name && el.value) {
        formData.append(el.name, el.value);
      }
    }

    try {
      await fetch('https://readdy.ai/api/form/d7cki2c5q56ra7tuf95g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SeoHead
        title="Contact Us | Mitigation Restoration Services Houston TX"
        description="Contact Mitigation Restoration Services in Houston TX. Available 24/7 for emergency cleanup, property restoration & pipe leak repair. Call +1 (800) 555-1234 or submit a request."
        keywords="contact restoration services Houston, emergency restoration contact, property damage Houston TX"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`}
        schema={contactSchema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#1E3A5F]">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/31a24baaf6c9fd7fa8c8cbdbb6227be9.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">
            <i className="ri-customer-service-fill"></i>
            Get In Touch
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Contact Us
          </h1>
          <p className="text-white/70 text-base max-w-xl mx-auto">
            Whether it&apos;s an emergency or a scheduled service, our team is ready to help. Reach out and we&apos;ll respond promptly.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-extrabold text-[#1E3A5F] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Get In Touch
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Our team is available 24/7 for emergency services. For non-emergency inquiries, we typically respond within 2 business hours.
              </p>

              <div className="space-y-5">
                {[
                  {
                    icon: 'ri-phone-fill',
                    label: 'Emergency Hotline',
                    value: '+1 (800) 555-1234',
                    sub: 'Available 24/7',
                    color: 'bg-[#DC2626]',
                  },
                  {
                    icon: 'ri-mail-fill',
                    label: 'Email Us',
                    value: 'info@mrsrestore.com',
                    sub: 'Response within 2 hours',
                    color: 'bg-[#1E3A5F]',
                  },
                  {
                    icon: 'ri-map-pin-fill',
                    label: 'Our Office',
                    value: '1234 Restoration Ave',
                    sub: 'Houston, TX 77001',
                    color: 'bg-teal-600',
                  },
                  {
                    icon: 'ri-time-fill',
                    label: 'Business Hours',
                    value: 'Mon–Fri: 8AM–6PM',
                    sub: 'Emergency: 24/7',
                    color: 'bg-amber-600',
                  },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${item.color} shrink-0`}>
                      <i className={`${item.icon} text-white text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                      <p className="font-semibold text-[#1E3A5F] text-sm">{item.value}</p>
                      <p className="text-gray-400 text-xs">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="mt-8 rounded-2xl overflow-hidden h-52">
                <iframe
                  title="MRS Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3464.0!2d-95.3698!3d29.7604!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDQ1JzM3LjQiTiA5NcKwMjInMTEuMyJX!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F8F9FA] rounded-2xl p-8">
                {submitted ? (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                      <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">Message Sent!</h3>
                    <p className="text-gray-500 text-sm">Thank you for reaching out. Our team will contact you within 2 business hours.</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 inline-flex items-center gap-2 bg-[#1E3A5F] text-white px-6 py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">Send Us a Message</h2>
                    <form
                      data-readdy-form
                      id="contact-form"
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name *</label>
                          <input
                            type="text"
                            name="full_name"
                            required
                            placeholder="John Smith"
                            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="john@example.com"
                            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                          <input
                            type="tel"
                            name="phone"
                            placeholder="+1 (555) 000-0000"
                            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Service Needed</label>
                          <select
                            name="service"
                            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1E3A5F] transition-colors cursor-pointer"
                          >
                            <option value="">Select a service</option>
                            <option value="Emergency Cleanup">Emergency Cleanup</option>
                            <option value="Property Restoration">Property Restoration</option>
                            <option value="Pipe Leak Repair">Pipe Leak Repair</option>
                            <option value="Drainage Cleaning">Drainage Cleaning</option>
                            <option value="Damage Assessment">Damage Assessment</option>
                            <option value="Mold Remediation">Mold Remediation</option>
                            <option value="Fire & Smoke Damage">Fire &amp; Smoke Damage</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Property Address</label>
                        <input
                          type="text"
                          name="property_address"
                          placeholder="123 Main St, Houston, TX"
                          className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Message *
                          <span className={`ml-2 font-normal ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>
                            {charCount}/500
                          </span>
                        </label>
                        <textarea
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          placeholder="Describe your situation and how we can help..."
                          onChange={(e) => setCharCount(e.target.value.length)}
                          className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A5F] transition-colors resize-none"
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Urgency Level</label>
                        <div className="flex flex-wrap gap-3">
                          {['Emergency (Now)', 'Urgent (Today)', 'Standard (This Week)', 'Scheduled'].map((level) => (
                            <label key={level} className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="urgency" value={level} className="accent-[#DC2626]" />
                              <span className="text-sm text-gray-700">{level}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={submitting || charCount > 500}
                        className="w-full bg-[#DC2626] text-white py-4 rounded-lg font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {submitting ? 'Sending...' : 'Send Message'}
                      </button>
                      <p className="text-center text-xs text-gray-400">
                        For emergencies, call directly: <a href="tel:+18005551234" className="text-[#DC2626] font-semibold">+1 (800) 555-1234</a>
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
